import { Button, Menu, Spinner } from '@design-systems/canvas-ui'
import Icon, { ChevronUpIcon, ErrorFillIcon } from '@design-systems/react-icons'
import { useEnv } from '@praxis/component-runtime-env'
import { CustomTooltip } from 'mvscommoncomponents'
import { useMemo } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  MANUFACTURING_TYPE_ID_FINISHED_GOOD,
  PARAM_LOCATION_ID,
  PARAM_MANUFACTURING_TYPE_ID,
  PARAM_SUPPLIER_ID,
  RFI_TYPE_LOCATION,
  ROUTE_VETTING_SUMMARY_LOCATION,
  ROUTE_VETTING_SUMMARY_SUPPLIER,
  STATUS_APPROVE_BUT_HOLD,
  STATUS_APPROVED,
  STATUS_COMPLETE_REVIEW,
  STATUS_INITIATED,
  STATUS_REJECTED,
  STATUS_SENT_BACK,
} from '../../../../common/commonConstants'
import {
  getApprovalButtonName,
  getControlVisibility,
} from '../../../../common/helper'
import { useData } from '../context/DataContext'
import { useUi, TYPES as UT } from '../context/UiContext'

export default function InternalApprovalButtons({
  rfiType,
  manufacturingTypeId,
  showTooltipForButton,
  onApproveClick,
  userSettings,
}) {
  const env = useEnv()
  const { state: d } = useData()
  const { state: u, dispatch: ud } = useUi()
  const navigate = useNavigate()
  const isNonCompliantOrArchived = u.isNonCompliant || u.isArchived

  const isSupplierMigratedOrApproved = () =>
    d?.rfiObject?.supplier_migrated ||
    [3, 15].includes(d?.rfiObject?.supplier_vetting_status_code)

  const getControlVisibiltyCondition = () => {
    return !(
      (u.isNBIOR &&
        getControlVisibility(
          'rfiNBIORActionButtons',
          'control',
          userSettings,
        )) ||
      !u.isNBIOR
    )
  }

  // NOTE: old code did NOT have a separate "rfiApprovalLauncher" gate,
  // so we rely solely on rfi1ActionButtons + the existing conditions.

  const isActionButtonDisabled = (status, btnType) => {
    let isDisabled = true
    if (!isNonCompliantOrArchived && !u.isUFLPA) {
      if (status && String(status).toLowerCase() === 'submitted') {
        if (rfiType === 'supplier') {
          isDisabled = false
        } else {
          if (['approve', 'reject', 'complete_review'].includes(btnType)) {
            if (isSupplierMigratedOrApproved()) {
              isDisabled = getControlVisibiltyCondition()
            }
          } else if (
            btnType === 'send back' &&
            String(
              d?.rfiObject?.supplier_vetting_status || '',
            ).toLowerCase() !== 'rejected'
          ) {
            isDisabled = getControlVisibiltyCondition()
          }
        }
      } else if (
        btnType === 'approve' &&
        d?.rfiObject?.vetting_status_code === 6
      ) {
        if (isSupplierMigratedOrApproved()) {
          isDisabled = getControlVisibiltyCondition()
        }
      }
    }
    return isDisabled
  }

  const capabilities = useMemo(
    () => d?.rfiObject?.capability_vetting_status ?? [],
    [d?.rfiObject?.capability_vetting_status],
  )

  const hasRenderableCapabilities = useMemo(
    () =>
      Array.isArray(capabilities) &&
      capabilities.some((c) => {
        const primary = c?.primary_type
        const secondary = c?.secondary_type
        const tertiary = c?.tertiary_type

        return (
          (primary === 'Footwear' ||
            (primary === 'Apparel' && secondary === 'Yarn')) &&
          !!secondary &&
          !!tertiary
        )
      }),
    [capabilities],
  )

  const onClickWithAction = (action) => {
    ud({ type: UT.SET_ACTION, payload: { userAction: action } })
    const actionLower = String(action || '').toLowerCase()
    const isEmailOnlyAction =
      actionLower === String(STATUS_REJECTED).toLowerCase() ||
      actionLower === String(STATUS_SENT_BACK).toLowerCase() ||
      actionLower === String(STATUS_COMPLETE_REVIEW).toLowerCase()

    if (isEmailOnlyAction) {
      ud({ type: UT.SET_MODAL, payload: { email: true } })
    } else {
      onApproveClick?.({
        action,
        manufacturingTypeId,
        hasRenderableCapabilities,
      })
    }
    ud({ type: UT.SET_LOADING, payload: { action: true, actionFailed: false } })
    ud({ type: UT.SET_ACTION_FAILED, payload: false })
  }

  const renderTooltipTitle = (tip) => {
    if (!tip?.showTooltip) return ''

    return <span className="approval-action-tooltip-text">{tip.errorMsg}</span>
  }

  const rejectTip = showTooltipForButton?.('reject')
  const sendBackTip = showTooltipForButton?.('send back')
  const approveTip = showTooltipForButton?.('approve')

  return (
    <div className="dock-right row-centre">
      <Button
        className="hc8-mr-3x"
        variant="ghost"
        size="md"
        onClick={() =>
          navigate(
            rfiType === RFI_TYPE_LOCATION
              ? ROUTE_VETTING_SUMMARY_LOCATION
              : ROUTE_VETTING_SUMMARY_SUPPLIER,
          )
        }
      >
        Close
      </Button>

      {!u.isNBIOR &&
        d?.template?.rfi3_required &&
        !u.fetchFailed &&
        rfiType === RFI_TYPE_LOCATION &&
        String(d?.rfiObject?.vetting_status || '').toLowerCase() ===
          'approved' && (
          <Button
            className="hc8-mr-3x"
            variant="primary"
            size="md"
            as={NavLink}
            to={`/internal/rfi3?${PARAM_LOCATION_ID}=${d?.rfiObject?.location_id}&${PARAM_SUPPLIER_ID}=${d?.rfiObject?.supplier_id}&${PARAM_MANUFACTURING_TYPE_ID}=${d?.rfiObject?.manufacturing_type_id}`}
          >
            {(String(d?.rfiObject?.rfi3_vetting_status || '').toLowerCase() ===
              STATUS_INITIATED ||
              String(d?.rfiObject?.rfi3_vetting_status || '').toLowerCase() ===
                STATUS_APPROVE_BUT_HOLD) &&
            !isNonCompliantOrArchived
              ? 'Complete RFI3'
              : 'View RFI3'}
          </Button>
        )}

      {getControlVisibility('rfi1ActionButtons', 'control', userSettings) && (
        <>
          <CustomTooltip
            title={renderTooltipTitle(rejectTip)}
            placement="top"
            arrow
            component={
              <Button
                className="hc8-mr-3x"
                variant="destructive"
                size="md"
                isDisabled={isActionButtonDisabled(
                  d?.rfiObject?.vetting_status,
                  'reject',
                )}
                onClick={() => onClickWithAction(STATUS_REJECTED)}
              >
                {String(u.userAction).toLowerCase() ===
                  String(STATUS_REJECTED).toLowerCase() &&
                u.loading.action &&
                !u.actionFailed ? (
                  <Spinner size="sm" />
                ) : (
                  'Reject'
                )}
                {String(u.userAction).toLowerCase() ===
                  String(STATUS_REJECTED).toLowerCase() &&
                  u.actionFailed && (
                    <Icon svg={ErrorFillIcon} className="hc8-clr-warning" />
                  )}
              </Button>
            }
          />

          {d?.template?.show_send_back && (
            <CustomTooltip
              title={renderTooltipTitle(sendBackTip)}
              placement="top"
              arrow
              component={
                <Button
                  className="hc8-mr-3x"
                  variant="secondary"
                  size="md"
                  isDisabled={isActionButtonDisabled(
                    d?.rfiObject?.vetting_status,
                    'send back',
                  )}
                  onClick={() => onClickWithAction(STATUS_SENT_BACK)}
                >
                  {String(u.userAction).toLowerCase() ===
                    String(STATUS_SENT_BACK).toLowerCase() &&
                  u.loading.action &&
                  !u.actionFailed ? (
                    <Spinner size="sm" />
                  ) : (
                    'Send Back'
                  )}
                  {String(u.userAction).toLowerCase() ===
                    String(STATUS_SENT_BACK).toLowerCase() &&
                    u.actionFailed && (
                      <Icon svg={ErrorFillIcon} className="hc8-clr-warning" />
                    )}
                </Button>
              }
            />
          )}

          {u.isNBIOR &&
          rfiType === RFI_TYPE_LOCATION &&
          String(d?.rfiObject?.manufacturing_type_id) ===
            String(MANUFACTURING_TYPE_ID_FINISHED_GOOD) ? (
            <Button
              className="hc8-mr-3x"
              variant="primary"
              size="md"
              isDisabled={isActionButtonDisabled(
                d?.rfiObject?.vetting_status,
                'complete_review',
              )}
              onClick={() => onClickWithAction(STATUS_COMPLETE_REVIEW)}
            >
              {String(u.userAction).toLowerCase() ===
                String(STATUS_COMPLETE_REVIEW).toLowerCase() &&
              u.loading.action &&
              !u.actionFailed ? (
                <Spinner size="sm" />
              ) : (
                'Complete Review'
              )}
              {String(u.userAction).toLowerCase() ===
                String(STATUS_COMPLETE_REVIEW).toLowerCase() &&
                u.actionFailed && (
                  <Icon svg={ErrorFillIcon} className="hc8-clr-warning" />
                )}
            </Button>
          ) : (
            <>
              {d?.template?.approval_action ? (
                <CustomTooltip
                  title={renderTooltipTitle(approveTip)}
                  placement="top"
                  arrow
                  component={
                    <Menu size="md" location="top-right">
                      <Menu.Toggle
                        size="md"
                        variant="primary"
                        isDisabled={isActionButtonDisabled(
                          d?.rfiObject?.vetting_status,
                          'approve',
                        )}
                      >
                        <div className="align-centre-centre">
                          {u.loading.action &&
                          !u.actionFailed &&
                          [STATUS_APPROVED, STATUS_APPROVE_BUT_HOLD].includes(
                            u.userAction,
                          ) ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              Approve
                              <Icon
                                svg={ChevronUpIcon}
                                className="hc8-ml-2x"
                                size="inline"
                              />
                            </>
                          )}
                        </div>
                      </Menu.Toggle>
                      {d?.template?.approval_action?.options?.map((option) => {
                        const status = env?.mfelcm?.vettingStatusMap?.[option]
                        return (
                          <Menu.Item
                            key={option}
                            className="hc8-fs-100 hc8-clr-brand hc8-txt-capitalize"
                            onClick={() =>
                              onClickWithAction((status || '').toUpperCase())
                            }
                          >
                            {getApprovalButtonName(status)}
                          </Menu.Item>
                        )
                      })}
                    </Menu>
                  }
                />
              ) : (
                <CustomTooltip
                  title={renderTooltipTitle(approveTip)}
                  placement="top"
                  arrow
                  component={
                    <Button
                      className="hc8-mr-3x"
                      variant="primary"
                      size="md"
                      isDisabled={isActionButtonDisabled(
                        d?.rfiObject?.vetting_status,
                        'approve',
                      )}
                      onClick={() => onClickWithAction(STATUS_APPROVED)}
                    >
                      {String(u.userAction).toLowerCase() ===
                        String(STATUS_APPROVED).toLowerCase() &&
                      u.loading.action &&
                      !u.actionFailed ? (
                        <Spinner size="sm" />
                      ) : (
                        'Approve'
                      )}
                      {String(u.userAction).toLowerCase() ===
                        String(STATUS_APPROVED).toLowerCase() &&
                        u.actionFailed && (
                          <Icon
                            svg={ErrorFillIcon}
                            className="hc8-clr-warning"
                          />
                        )}
                    </Button>
                  }
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
